import {
  ApiRoute,
  Invitation,
  InvitationSchema,
  InvitationWithDetail,
  InvitationWithDetailSchema,
  getApiRoute
} from "@garther/interfaces";

import { API_BASE_URL } from "../configs";
import { BaseApiClient } from "./base-client";

export class InvitationApiClient extends BaseApiClient {
  constructor() {
    super({
      baseURL: API_BASE_URL,
      baseEndpoint: getApiRoute(ApiRoute.INVITE)
    });
  }

  /**
   * Get invitation by ID
   */
  async getInvitationById(invitationId: string): Promise<Invitation> {
    return await this.get(`/${invitationId}`, InvitationSchema, { requireAuth: false });
  }

  /**
   * Get invitation with details by ID
   */
  async getInvitationWithDetails(id: string): Promise<InvitationWithDetail> {
    return await this.get(`/${id}/details`, InvitationWithDetailSchema, {
      requireAuth: false
    });
  }

  /**
   * Get invitation by booking ID
   */
  async getInvitationByBookingId(bookingId: string): Promise<Invitation> {
    return await this.get(`/booking/${bookingId}`, InvitationSchema, {
      requireAuth: false
    });
  }

  /**
   * Create a friend invitation for a booking
   */
  async createInvitation(bookingId: string): Promise<Invitation> {
    return await this.post("/create", InvitationSchema, { bookingId }, { requireAuth: true });
  }

  /**
   * Confirm friend invitation
   */
  async confirmInvitation(invitationId: string): Promise<Invitation> {
    return await this.post("/confirm", InvitationSchema, { invitationId }, { requireAuth: true });
  }

  /**
   * Decline friend invitation
   */
  async declineInvitation(invitationId: string): Promise<Invitation> {
    return await this.post("/decline", InvitationSchema, { invitationId }, { requireAuth: true });
  }

  /**
   * Generate shareable invitation link
   */
  generateInvitationLink(invitationCode: string): string {
    return `${window.location.origin}/invite/${invitationCode}`;
  }

  /**
   * Copy invitation link to clipboard
   */
  async copyInvitationLinkToClipboard(invitationCode: string): Promise<void> {
    const link = this.generateInvitationLink(invitationCode);

    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(link);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }
  }
}

export const invitationApiClient = new InvitationApiClient();
